import { Completion, CompletionContext, CompletionResult, CompletionSource } from "@codemirror/autocomplete";
import { IOperatorGuideEntry } from "../utils/operatorGuideData";

export interface IQuestionVariable {
  id: number;
  variable: string;
  question: string;
  question_order_text: string;
}

export interface IQuestionResponseVariable {
  id: number;
  variable: string;
  question: string;
  response: string;
}

export interface IRankOption {
  name: string;
}

// `Completion.type` is repurposed here purely as a per-row style hook — NOT for its usual
// "pick an icon" purpose (icons are disabled entirely in EquationCodeMirrorEditor's
// autocompletion() config). EquationCodeMirrorEditor's `optionClass` reads this field back to
// bold the label for variable/rank rows, matching the old .entryName treatment, while leaving
// operator rows (no `type` set) plain — exactly like the old ":" trigger's unstyled rendering.
export const RQL_VARIABLE_OPTION_TYPE = "rql-variable";
export const RQL_RANK_OPTION_TYPE = "rql-rank";

// Minimum plain-text run length before the untriggered, "type anywhere" source (see
// makeGenericSource below) kicks in — the @/#/:/$ triggers have no such minimum since typing the
// trigger character itself is already an explicit signal.
const MIN_GENERIC_WORD_LENGTH = 1;

// Shared row-builders — used by BOTH a trigger's dedicated source (e.g. "@") and the untriggered
// generic source (see makeGenericSource) so a variable/operator/rank looks and behaves
// identically no matter which path surfaced it.
const questionVariableCompletion = (
  item: IQuestionVariable,
  onVariableSelected: (variable: string) => void,
): Completion => ({
  label: item.variable,
  type: RQL_VARIABLE_OPTION_TYPE,
  detail: `${item.question_order_text}) ${item.question}`,
  apply: (view, _completion, from, to) => {
    view.dispatch({
      changes: { from, to, insert: item.variable },
      selection: { anchor: from + item.variable.length },
    });
    onVariableSelected(item.variable);
  },
});

const responseVariableCompletion = (
  item: IQuestionResponseVariable,
  onVariableSelected: (variable: string) => void,
): Completion => ({
  label: item.variable,
  type: RQL_VARIABLE_OPTION_TYPE,
  detail: item.response,
  apply: (view, _completion, from, to) => {
    view.dispatch({
      changes: { from, to, insert: item.variable },
      selection: { anchor: from + item.variable.length },
    });
    onVariableSelected(item.variable);
  },
});

const operatorCompletion = (entry: IOperatorGuideEntry): Completion => {
  const insertText = `${entry.name}()`;
  return {
    label: entry.name,
    detail: entry.syntax,
    info: entry.description,
    apply: (view, _completion, from, to) => {
      view.dispatch({
        changes: { from, to, insert: insertText },
        // insertionStart (from) + insertedText.length - 1 lands the cursor immediately before
        // the closing ")", ready to type the operator's arguments.
        selection: { anchor: from + insertText.length - 1 },
      });
    },
  };
};

const rankCompletion = (rank: IRankOption): Completion => ({
  label: rank.name,
  type: RQL_RANK_OPTION_TYPE,
  apply: (view, _completion, from, to) => {
    view.dispatch({
      changes: { from, to, insert: rank.name },
      selection: { anchor: from + rank.name.length },
    });
  },
});

const matchesToken = (haystack: string, token: string): boolean =>
  haystack.toLowerCase().includes(token);

// @ — question variables: matches today's questionDataProvider (substring on variable OR
// question text), two-line dropdown row (bold variable + "{order}) {question}"), raw insert, no
// caret repositioning, and fires onVariableSelected (-> store.setScrollToVariable) on pick.
export const makeQuestionVariableSource = (
  getList: () => IQuestionVariable[],
  onVariableSelected: (variable: string) => void,
): CompletionSource => {
  return (context: CompletionContext): CompletionResult | null => {
    const match = context.matchBefore(/@[^\s(),"']*/);
    if (!match) {
      return null;
    }
    const token = match.text.slice(1).toLowerCase();
    const options = getList()
      .filter(
        (item) =>
          matchesToken(item.variable, token) ||
          matchesToken(item.question, token),
      )
      .map((item) => questionVariableCompletion(item, onVariableSelected));
    if (!options.length) {
      return null;
    }
    return { from: match.from, to: match.to, options, filter: false };
  };
};

// # — response variables: same filter shape as questionResponseDataProvider. Row shows the
// response's own answer text (e.g. "Yes") as a detail line below the variable, matching @'s
// question-detail treatment. Raw insert, also fires onVariableSelected.
export const makeResponseVariableSource = (
  getList: () => IQuestionResponseVariable[],
  onVariableSelected: (variable: string) => void,
): CompletionSource => {
  return (context: CompletionContext): CompletionResult | null => {
    const match = context.matchBefore(/#[^\s(),"']*/);
    if (!match) {
      return null;
    }
    const token = match.text.slice(1).toLowerCase();
    const options = getList()
      .filter(
        (item) =>
          matchesToken(item.variable, token) ||
          matchesToken(item.question, token),
      )
      .map((item) => responseVariableCompletion(item, onVariableSelected));
    if (!options.length) {
      return null;
    }
    return { from: match.from, to: match.to, options, filter: false };
  };
};

// : — operators: matches today's operatorDataProvider filtering, but now shows each operator's
// syntax (e.g. "AND(response_variable_1, response_variable_2, ...)") as a detail line AND its
// full description in CodeMirror's info panel (shown alongside whichever row is highlighted) —
// the same operatorGuideData.ts entries the Operator Guide modal reads, so a user sees both the
// shape and the meaning of an operator without leaving the equation, and the two can never drift
// out of sync. Inserts "Name()" and lands the cursor INSIDE the parens in the same dispatch —
// replaces the old setTimeout + caret-back-one hack, which existed only to dodge
// react-textarea-autocomplete's async caret timing.
export const makeOperatorSource = (
  operatorEntries: readonly IOperatorGuideEntry[],
): CompletionSource => {
  return (context: CompletionContext): CompletionResult | null => {
    const match = context.matchBefore(/:[^\s(),"']*/);
    if (!match) {
      return null;
    }
    const token = match.text.slice(1).toLowerCase();
    const options = operatorEntries
      .filter((entry) => matchesToken(entry.name, token))
      .map((entry) => operatorCompletion(entry));
    if (!options.length) {
      return null;
    }
    return { from: match.from, to: match.to, options, filter: false };
  };
};

// $ — rank names (for IMPORT): getRankOptions() is expected to ALREADY exclude the current tab's
// own rank and any strictly-higher-order rank (the tier-order rule) — this source only applies
// the typed-substring filter on top of that pre-filtered eligible set. Raw insert, no
// onVariableSelected call (ranks don't scroll the Question Snapshot panel).
export const makeRankSource = (
  getRankOptions: () => IRankOption[],
): CompletionSource => {
  return (context: CompletionContext): CompletionResult | null => {
    const match = context.matchBefore(/\$[^\s(),"']*/);
    if (!match) {
      return null;
    }
    const token = match.text.slice(1).toLowerCase();
    const options = getRankOptions()
      .filter((rank) => matchesToken(rank.name, token))
      .map((rank) => rankCompletion(rank));
    if (!options.length) {
      return null;
    }
    return { from: match.from, to: match.to, options, filter: false };
  };
};

// Untriggered, "type anywhere" source — an addition ALONGSIDE the @/#/:/$ shortcuts above, not a
// replacement for them. Once the user has typed minWordLength+ plain characters (no trigger
// prefix), this searches operators, question variables, response variables, and ranks all at
// once and merges them into one list, reusing the exact same per-category row builders as the
// dedicated trigger sources so each entry looks and behaves identically either way it was found.
// Skips entries immediately preceded by an @/#/:/$ — that word is already being handled by its
// own dedicated source above, and letting both fire would show duplicate, differently-ranged
// option sets for the same word.
export const makeGenericSource = (
  getQuestionVariableList: () => IQuestionVariable[],
  getResponseVariableList: () => IQuestionResponseVariable[],
  getRankOptions: () => IRankOption[],
  operatorEntries: readonly IOperatorGuideEntry[],
  onVariableSelected: (variable: string) => void,
  minWordLength: number = MIN_GENERIC_WORD_LENGTH,
): CompletionSource => {
  const triggerCharacters = new Set(["@", "#", ":", "$"]);
  return (context: CompletionContext): CompletionResult | null => {
    // Same "anything but whitespace/parens/comma/quotes" character class the dedicated @/#/$
    // sources use for the rest of their token (not just [A-Za-z0-9_]) — real variable names can
    // contain hyphens and other symbols, and a narrower class here fragments the match mid-word:
    // the popup would flicker closed at the "-" in "hia-_billju_0_no", and picking a suggestion
    // afterwards would only replace the piece after the hyphen, duplicating the already-typed
    // prefix. The leading character is excluded from starting on a trigger character itself, so
    // this never swallows the "@"/"#"/":"/"$" of an adjacent dedicated-trigger match.
    const match = context.matchBefore(/[^\s(),"'@#:$][^\s(),"']*/);
    if (!match || match.text.length < minWordLength) {
      return null;
    }
    const precedingChar =
      match.from > 0
        ? context.state.sliceDoc(match.from - 1, match.from)
        : "";
    if (triggerCharacters.has(precedingChar)) {
      return null;
    }

    const token = match.text.toLowerCase();

    const options: Completion[] = [
      ...operatorEntries
        .filter((entry) => matchesToken(entry.name, token))
        .map((entry) => operatorCompletion(entry)),
      ...getQuestionVariableList()
        .filter(
          (item) =>
            matchesToken(item.variable, token) ||
            matchesToken(item.question, token),
        )
        .map((item) => questionVariableCompletion(item, onVariableSelected)),
      ...getResponseVariableList()
        .filter(
          (item) =>
            matchesToken(item.variable, token) ||
            matchesToken(item.question, token) ||
            matchesToken(item.response, token),
        )
        .map((item) => responseVariableCompletion(item, onVariableSelected)),
      ...getRankOptions()
        .filter((rank) => matchesToken(rank.name, token))
        .map((rank) => rankCompletion(rank)),
    ];

    if (!options.length) {
      return null;
    }
    return { from: match.from, to: match.to, options, filter: false };
  };
};
